import { csrfTokenHeader } from "@circle-react/helpers/csrfToken";

const CSRF_PROTECTED_METHODS = new Set(["DELETE", "PATCH", "POST", "PUT"]);

const getAnalyticsTrackerToken = (): string | undefined =>
  window.ANALYTICS_TRACKER_API_TOKEN;

const hasHeader = (headers: Record<string, string>, header: string): boolean =>
  new Headers(headers).has(header);

export const omitHeaders = (
  headers: Record<string, string>,
  omittedHeaders: readonly string[] = [],
): Record<string, string> => {
  const omittedHeaderNames = new Set(
    omittedHeaders.map(header => header.toLowerCase()),
  );

  return Object.fromEntries(
    Object.entries(headers).filter(
      ([header]) => !omittedHeaderNames.has(header.toLowerCase()),
    ),
  );
};

export const isSameOrigin = (url: string): boolean => {
  try {
    const parsed = new URL(url, window.location.origin);
    return parsed.origin === window.location.origin;
  } catch {
    // If URL parsing fails, assume same-origin to avoid blocking the request
    return true;
  }
};

const withFrontendVersionHeader = ({
  headers,
  url,
}: {
  headers: Record<string, string>;
  url: string;
}): Record<string, string> => {
  if (!isSameOrigin(url) || hasHeader(headers, "X-Circle-Frontend-Version")) {
    return headers;
  }

  return {
    ...headers,
    "X-Circle-Frontend-Version": window.CIRCLE_APP_VERSION || "unknown",
  };
};

const withCsrfTokenHeader = ({
  headers,
  method,
  url,
}: {
  headers: Record<string, string>;
  method: string;
  url: string;
}): Record<string, string> => {
  if (
    !CSRF_PROTECTED_METHODS.has(method.toUpperCase()) ||
    !isSameOrigin(url) ||
    hasHeader(headers, "X-CSRF-Token")
  ) {
    return headers;
  }

  return { ...headers, ...csrfTokenHeader() };
};

export const buildBackendRequestHeaders = ({
  headers: requestHeaders,
  method,
  sameOriginHeaders,
  url,
}: {
  headers?: Record<string, string>;
  method: string;
  sameOriginHeaders?: readonly string[];
  url: string;
}): Record<string, string> => {
  let headers: Record<string, string> = {
    "Content-Type": "application/json",
    Accept: "application/json",
    ...requestHeaders,
  };

  if (!isSameOrigin(url)) {
    headers = omitHeaders(headers, sameOriginHeaders);
  }

  const token = getAnalyticsTrackerToken();
  if (token && isSameOrigin(url) && !headers.Authorization) {
    headers.Authorization = `Bearer ${token}`;
  }

  headers = withFrontendVersionHeader({ headers, url });
  return withCsrfTokenHeader({ headers, method, url });
};
